import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, FormControl, InputLabel, Select, MenuItem, Button,
    TablePagination, Chip, Tooltip, Alert
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { ISalaryPayment, JournalPostStatus } from '../../interfaces/salaryPayment';
import { UserType } from '../../interfaces/userType.enum';
import SkipJournalDialog from './SkipJournalDialog';
import { useTranslation } from 'react-i18next';

type FilterStatus = 'ALL' | 'FAILED' | 'SKIPPED' | 'PENDING';

const STATUS_COLORS: Record<JournalPostStatus, 'default' | 'warning' | 'error' | 'success'> = {
    PENDING: 'warning',
    POSTED: 'success',
    FAILED: 'error',
    SKIPPED: 'default',
};

const truncate = (s: string | null | undefined, n = 80) => {
    if (!s) return '-';
    return s.length > n ? s.slice(0, n) + '…' : s;
};

const PaymentReconciliation: React.FC = () => {
    const { t } = useTranslation(['payroll', 'common']);
    const [filter, setFilter] = useState<FilterStatus>('FAILED');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [payments, setPayments] = useState<ISalaryPayment[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [skipTargetId, setSkipTargetId] = useState<string | null>(null);

    const { showSuccess, showApiError } = useToast();
    const { user } = useAuth();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const isInstitutionAdmin = userRoles.includes(UserType.InstitutionAdmin);
    const canCreatePayment = usePermission('PAYROLL_PAYMENT_CREATE', isInstitutionAdmin);
    const canOverride = usePermission('PAYROLL_PAYMENT_OVERRIDE', isInstitutionAdmin);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            if (filter === 'ALL') {
                // Fetch the three non-POSTED statuses in parallel and merge client-side
                const [failed, skipped, pending] = await Promise.all([
                    PayrollService.getPaymentReconciliation(institutionId, 'FAILED', 0, 200),
                    PayrollService.getPaymentReconciliation(institutionId, 'SKIPPED', 0, 200),
                    PayrollService.getPaymentReconciliation(institutionId, 'PENDING', 0, 200),
                ]);
                const merged = [...failed.content, ...skipped.content, ...pending.content];
                const start = page * size;
                setPayments(merged.slice(start, start + size));
                setTotalElements(merged.length);
            } else {
                const res = await PayrollService.getPaymentReconciliation(institutionId, filter, page, size);
                setPayments(res.content);
                setTotalElements(res.totalElements);
            }
        } catch (error) {
            showApiError(error, t('reconciliation.toast.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [institutionId, filter, page, size, showApiError, t]);

    useEffect(() => {
        load();
    }, [load]);

    const handleFilterChange = (v: FilterStatus) => {
        setFilter(v);
        setPage(0);
    };

    const handleRetry = async (paymentId: string) => {
        try {
            await PayrollService.retryJournalPost(paymentId);
            showSuccess(t('shared.journalRetrySucceeded'));
            load();
        } catch (error) {
            showApiError(error, t('shared.retryFailed'));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="h5">{t('reconciliation.title')}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('shared.selectInstitution')}</InputLabel>
                        <Select
                            value={institutionId}
                            onChange={(e) => setInstitutionId(e.target.value)}
                            label={t('shared.selectInstitution')}
                        >
                            {institutions.map((inst) => (
                                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="reconciliation-filter-label">{t('common:fields.status')}</InputLabel>
                    <Select
                        labelId="reconciliation-filter-label"
                        label={t('common:fields.status')}
                        value={filter}
                        onChange={(e) => handleFilterChange(e.target.value as FilterStatus)}
                        inputProps={{ 'aria-label': t('common:fields.status') }}
                    >
                        <MenuItem value="ALL">{t('reconciliation.filter.all')}</MenuItem>
                        <MenuItem value="FAILED">{t('reconciliation.filter.failed')}</MenuItem>
                        <MenuItem value="SKIPPED">{t('reconciliation.filter.skipped')}</MenuItem>
                        <MenuItem value="PENDING">{t('reconciliation.filter.pending')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {filter === 'ALL' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('reconciliation.allInfo')}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('shared.employee')}</TableCell>
                            <TableCell align="right">{t('common:fields.amount')}</TableCell>
                            <TableCell>{t('shared.paymentDate')}</TableCell>
                            <TableCell>{t('shared.method')}</TableCell>
                            <TableCell>{t('reconciliation.columns.journalStatus')}</TableCell>
                            <TableCell align="right">{t('reconciliation.columns.attempts')}</TableCell>
                            <TableCell>{t('reconciliation.columns.lastError')}</TableCell>
                            <TableCell>{t('reconciliation.columns.lastTry')}</TableCell>
                            <TableCell align="right">{t('common:fields.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">{t('shared.loading')}</TableCell>
                            </TableRow>
                        )}
                        {!loading && payments.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    {t('reconciliation.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && payments.map(p => {
                            const isPosted = p.journalPostStatus === 'POSTED';
                            const showRetry = canCreatePayment && !isPosted;
                            const showSkip = canOverride && !isPosted;
                            return (
                                <TableRow key={p.id}>
                                    <TableCell>{p.employeeId}</TableCell>
                                    <TableCell align="right">{Number(p.amount).toFixed(2)}</TableCell>
                                    <TableCell>{p.paymentDate}</TableCell>
                                    <TableCell>{t(`paymentMethod.${p.paymentMethod}`)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={t(`journalStatus.${p.journalPostStatus}`)}
                                            color={STATUS_COLORS[p.journalPostStatus]}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{p.journalPostAttempts}</TableCell>
                                    <TableCell>
                                        <Tooltip title={p.journalPostError || ''}>
                                            <span>{truncate(p.journalPostError)}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{p.journalPostLastTry || '-'}</TableCell>
                                    <TableCell align="right">
                                        {showRetry && (
                                            <Button
                                                size="small"
                                                onClick={() => handleRetry(p.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                {t('shared.retryPost')}
                                            </Button>
                                        )}
                                        {showSkip && (
                                            <Button
                                                size="small"
                                                color="warning"
                                                onClick={() => setSkipTargetId(p.id)}
                                            >
                                                {t('shared.skip')}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            <SkipJournalDialog
                open={skipTargetId !== null}
                onClose={() => setSkipTargetId(null)}
                paymentId={skipTargetId || ''}
                onSuccess={load}
            />
        </Box>
    );
};

export default PaymentReconciliation;
